import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { usePostHog } from 'posthog-js/react'
import { trpc } from '@/config/trpc'
import { Check, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'

const LIMIT_MESSAGES: Record<string, string> = {
  post: "You've used all your posts & downloads for this period.",
  image: "You've used all your image generations for this period.",
  chat: "You've used all your chat messages for this period.",
  brands: "You've reached the maximum number of brands on your plan.",
  moodboards: "You've reached the maximum number of moodboards on your plan.",
  automations: "You've reached the maximum number of automations on your plan.",
  model: 'This model is only available on paid plans.',
  proModel: 'This model is only available on the Pro plan.',
}

const UPGRADE_HIGHLIGHTS = [
  'Access to more models',
  'Unlimited Posts',
  'More Image Generations & Chat Messages',
  'More Brands & Automations',
  'Unlimited Moodboards',
  'MCP Access',
]

/** Where in the product the user hit the wall. Paired with `limitType` this
 *  answers "what were they trying to do when we asked them to pay", which is
 *  the question the pricing-page funnel alone cannot answer. */
export type UpgradeSurface =
  | 'home'
  | 'home_brainstorm'
  | 'workspace_chat'
  | 'workspace_editor'
  | 'editor'
  | 'moodboard_list'
  | 'brand_new'
  | 'brand_switcher'

interface Props {
  open: boolean
  onClose: () => void
  limitType: string
  surface: UpgradeSurface
}

export function UpgradeModal({ open, onClose, limitType, surface }: Props) {
  const navigate = useNavigate()
  const posthog = usePostHog()

  // AppSumo users hitting a cap can't buy Standard (their lifetime plan
  // outranks Stripe) — their upgrade path is a higher tier on AppSumo.
  const { data: userSubscription } = trpc.userSubscription.get.useQuery(undefined, {
    enabled: open,
  })
  const isAppsumo = userSubscription?.isSubscribed && userSubscription.source === 'appsumo'
  // Tier 3 is the top of the ladder: the only wall it can hit is the hidden
  // fair-use ceiling (FAIR_USE_CAPS), so "upgrade on AppSumo" would be a lie.
  const isFairUse = isAppsumo && userSubscription.tier === 'appsumo-tier-3'

  // This is the moment of intent — the only point where we know a user was
  // blocked and why. Fires once per open rather than per render.
  useEffect(() => {
    if (!open) return
    posthog.capture('upgrade_prompt_shown', { limitType, surface, fairUse: isFairUse })
  }, [open, limitType, surface, isFairUse, posthog])

  // `onClose` is shared by the X, the backdrop, "Maybe Later" and "Upgrade Now",
  // so the reason has to be captured at each call site rather than inferred.
  const dismiss = (action: 'maybe_later' | 'closed') => {
    posthog.capture('upgrade_prompt_dismissed', { limitType, surface, action })
    onClose()
  }

  const accept = () => {
    posthog.capture('upgrade_prompt_accepted', { limitType, surface })
    onClose()
    if (isAppsumo) {
      window.open('https://appsumo.com/account/products/', '_blank', 'noopener')
      return
    }
    navigate('/pricing')
  }

  if (isFairUse) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && dismiss('closed')}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>You&apos;ve reached this month&apos;s fair-use limit</DialogTitle>
            <DialogDescription>
              Your lifetime deal includes unlimited usage under a fair-use policy. You&apos;ve hit
              this month&apos;s ceiling, and it resets automatically at the start of your next
              period. If you need more, reach out to our support and we&apos;ll sort it out.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => dismiss('closed')}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss('closed')}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            You&apos;ve reached your plan limit
          </DialogTitle>
          <DialogDescription>
            {LIMIT_MESSAGES[limitType] || "You've reached a limit on your plan."}
          </DialogDescription>
        </DialogHeader>

        {isAppsumo ? (
          <div className="space-y-3 py-2">
            <p className="text-sm">
              Your AppSumo lifetime deal has monthly limits. Upgrade to a higher tier on AppSumo for
              more posts, image generations, chats, and brands.
            </p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium">Upgrade your plan for:</p>
            <ul className="space-y-2">
              {UPGRADE_HIGHLIGHTS.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 shrink-0 text-emerald-500" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => dismiss('maybe_later')}>
            Maybe Later
          </Button>
          <Button onClick={accept}>
            <Sparkles className="mr-2 h-4 w-4" />
            {isAppsumo ? 'Upgrade on AppSumo' : 'Upgrade Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
